import { useEffect } from "react";

/**
 * Lightweight SEO hook — updates <title>, meta description, canonical, and OG/Twitter tags
 * on route change. Works well for SPAs because modern search crawlers (Googlebot, Bingbot)
 * execute JavaScript. On unmount we restore the previous values to avoid stale SEO leaking
 * between routes.
 *
 * Usage:
 *   useSeo({ title: "About — Videoepreneur", description: "...", image: "...", noIndex: false })
 */
export default function useSeo({
  title,
  description,
  image,
  url,
  noIndex = false,
  type = "website",
} = {}) {
  useEffect(() => {
    const prev = { title: document.title, tags: {} };

    const setMeta = (selector, attr, value) => {
      if (value == null) return;
      let el = document.head.querySelector(selector);
      if (!el) {
        el = document.createElement("meta");
        const [k, v] = selector.replace("meta[", "").replace("]", "").split("=");
        el.setAttribute(k, v.replace(/"/g, ""));
        document.head.appendChild(el);
      }
      prev.tags[selector] = el.getAttribute(attr);
      el.setAttribute(attr, value);
    };

    const setLink = (rel, href) => {
      if (!href) return;
      let el = document.head.querySelector(`link[rel="${rel}"]`);
      if (!el) {
        el = document.createElement("link");
        el.setAttribute("rel", rel);
        document.head.appendChild(el);
      }
      prev.tags[`link[rel="${rel}"]`] = el.getAttribute("href");
      el.setAttribute("href", href);
    };

    if (title) document.title = title;
    if (description) {
      setMeta('meta[name="description"]', "content", description);
      setMeta('meta[property="og:description"]', "content", description);
      setMeta('meta[name="twitter:description"]', "content", description);
    }
    if (title) {
      setMeta('meta[property="og:title"]', "content", title);
      setMeta('meta[name="twitter:title"]', "content", title);
    }
    if (image) {
      setMeta('meta[property="og:image"]', "content", image);
      setMeta('meta[name="twitter:image"]', "content", image);
    }
    if (type) setMeta('meta[property="og:type"]', "content", type);
    setMeta('meta[name="robots"]', "content", noIndex ? "noindex, nofollow" : "index, follow, max-image-preview:large, max-snippet:-1");

    const canonical = url || (typeof window !== "undefined" ? window.location.href.split("#")[0].split("?")[0] : undefined);
    if (canonical) {
      setLink("canonical", canonical);
      setMeta('meta[property="og:url"]', "content", canonical);
    }

    return () => {
      // Restore previous values so navigating away doesn't leak per-page SEO
      document.title = prev.title;
      for (const [sel, val] of Object.entries(prev.tags)) {
        const el = document.head.querySelector(sel);
        if (el && val != null) {
          el.setAttribute(sel.startsWith("link") ? "href" : "content", val);
        }
      }
    };
  }, [title, description, image, url, noIndex, type]);
}
